// Pure bit-mapping algorithms shared by the bit-grid visualization and the
// signal decoder, so both always agree on exactly which physical bits a
// signal occupies. Implements the real DBC Motorola numbering convention
// (a byte-wise "zigzag" traversal) rather than a reversed Intel mapping.
//
// Terminology: a "physical" bit position is { byteIndex, bitInByte }, where
// bitInByte is counted 0 (LSB of that byte) .. 7 (MSB of that byte) — this
// matches the "7 6 5 4 3 2 1 0" header convention used by the bit grid. A
// "DBC number" is the monotonic bit index (0..63 for an 8-byte frame) that
// the .dbc StartBit field is expressed in, which counts differently
// depending on byte order.

export const BYTE_ORDER = { INTEL: 'intel', MOTOROLA: 'motorola' };

// Intel numbers bits LSB-up through the byte stream (byte*8 + bitInByte).
// Motorola numbers them MSB-down within each byte, wrapping to the next
// byte's MSB (byte*8 + (7 - bitInByte)).
export function dbcNumber(byteIndex, bitInByte, byteOrder) {
  return byteOrder === BYTE_ORDER.MOTOROLA
    ? byteIndex * 8 + (7 - bitInByte)
    : byteIndex * 8 + bitInByte;
}

function physicalPosition(dbcNum, byteOrder) {
  const byteIndex = Math.floor(dbcNum / 8);
  const withinByte = dbcNum - byteIndex * 8;
  const bitInByte = byteOrder === BYTE_ORDER.MOTOROLA ? 7 - withinByte : withinByte;
  return { byteIndex, bitInByte };
}

// Ordered physical bit positions for a signal, from MSB (index 0) to LSB
// (index length - 1).
//
// `startBit` is the .dbc StartBit field, interpreted per byte order exactly
// as real DBC files define it: for Intel it addresses the signal's LSB; for
// Motorola it addresses the signal's MSB. Because dbcNumber() is monotonic
// increasing along each order's own traversal, walking startBit..startBit+
// length-1 and mapping each back to a physical position always produces the
// correct contiguous run for that order.
export function getSignalBitPositions(startBit, length, byteOrder) {
  const positions = [];
  for (let i = 0; i < length; i++) {
    positions.push(physicalPosition(startBit + i, byteOrder));
  }
  // For Intel the walk above proceeds LSB->MSB (ascending significance), so
  // reverse it to the shared MSB-first convention. For Motorola the walk is
  // already MSB->LSB.
  return byteOrder === BYTE_ORDER.MOTOROLA ? positions : positions.reverse();
}

export function cellKey(byteIndex, bitInByte) {
  return `${byteIndex}-${bitInByte}`;
}

export function highestByteIndex(positions) {
  return positions.reduce((max, p) => Math.max(max, p.byteIndex), -1);
}

// Given two clicked physical cells and the byte order to interpret the
// selection under, derive the contiguous DBC start bit / length spanning
// both cells (inclusive). This is always well-formed by construction: any
// two physical cells map to two DBC numbers, and every DBC number between
// them corresponds to exactly one physical cell under that byte order.
export function computeSelectionRange(cellA, cellB, byteOrder) {
  const dA = dbcNumber(cellA.byteIndex, cellA.bitInByte, byteOrder);
  const dB = dbcNumber(cellB.byteIndex, cellB.bitInByte, byteOrder);
  const startBit = Math.min(dA, dB);
  const length = Math.abs(dA - dB) + 1;
  return { startBit, length };
}
